import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Target,         // Lead Generation
  UserPlus,       // Client Intake
  Briefcase,      // HR Hiring
  Wrench,         // Service Requests
  CheckSquare,    // Internal Approvals
  BarChart2,      // Surveys
  CalendarCheck,  // Registration Forms
  ArrowRight,
} from 'lucide-react';

/**
 * UseCasesSection — high-conversion use-case cards. Every card has an
 * icon, a short benefit-led copy line, and a CTA that links to a
 * matching pre-built template or the signup flow.
 */

const USE_CASES = [
  {
    Icon: Target,
    title: 'Lead Generation',
    desc: 'Capture qualified leads and route them to sales automatically.',
    cta: 'Build a lead form',
    href: '/templates?category=Contact%20%26%20Leads',
    accent: 'from-indigo-500 to-violet-500',
    tint:   'bg-indigo-50/60 ring-indigo-100',
  },
  {
    Icon: UserPlus,
    title: 'Client Intake',
    desc: 'Onboard new clients with smart, multi-step intake forms.',
    cta: 'Create intake form',
    href: '/templates?category=Business%20%26%20Office',
    accent: 'from-emerald-500 to-teal-500',
    tint:   'bg-emerald-50/60 ring-emerald-100',
  },
  {
    Icon: Briefcase,
    title: 'HR Hiring',
    desc: 'Collect applications, screen candidates, and trigger interviews.',
    cta: 'Launch hiring form',
    href: '/templates?category=HR%20%26%20Jobs',
    accent: 'from-violet-500 to-fuchsia-500',
    tint:   'bg-violet-50/60 ring-violet-100',
  },
  {
    Icon: Wrench,
    title: 'Service Requests',
    desc: 'Ticket every customer issue with priority routing built in.',
    cta: 'Open a ticket form',
    href: '/templates?category=IT%20%26%20Technical',
    accent: 'from-amber-500 to-orange-500',
    tint:   'bg-amber-50/60 ring-amber-100',
  },
  {
    Icon: CheckSquare,
    title: 'Internal Approvals',
    desc: 'Multi-step sign-offs for purchases, leave, expenses, and more.',
    cta: 'Set up approvals',
    href: '/templates?category=Business%20%26%20Office',
    accent: 'from-rose-500 to-pink-500',
    tint:   'bg-rose-50/60 ring-rose-100',
  },
  {
    Icon: BarChart2,
    title: 'Surveys',
    desc: 'Collect feedback with conditional logic and real-time analytics.',
    cta: 'Build a survey',
    href: '/templates?category=Feedback%20%26%20Surveys',
    accent: 'from-cyan-500 to-blue-500',
    tint:   'bg-cyan-50/60 ring-cyan-100',
  },
  {
    Icon: CalendarCheck,
    title: 'Registration Forms',
    desc: 'Event sign-ups with confirmations, reminders, and seat limits.',
    cta: 'Open registrations',
    href: '/templates?category=Events',
    accent: 'from-fuchsia-500 to-pink-500',
    tint:   'bg-fuchsia-50/60 ring-fuchsia-100',
  },
];

export const UseCasesSection = () => {
  return (
    <section
      className="py-20 lg:py-24 bg-slate-50"
      data-testid="use-cases-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-indigo-600 mb-3">
            Use cases
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            One platform.{' '}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Every workflow.
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            From the first lead to the final signed approval — Formlio adapts to every form your team needs.
          </p>
        </motion.div>

        {/* Card grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
          {USE_CASES.map((u, i) => {
            const { Icon, title, desc, cta, href, accent, tint } = u;
            return (
              <motion.article
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.4, delay: 0.05 + i * 0.05 }}
                whileHover={{ y: -6 }}
                className={`group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 overflow-hidden flex flex-col`}
                data-testid={`use-case-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {/* Decorative tint blob behind the icon */}
                <span
                  aria-hidden
                  className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${tint} ring-1 opacity-80 group-hover:opacity-100 transition-opacity blur-[1px]`}
                />

                {/* Icon */}
                <div className="relative mb-4">
                  <motion.span
                    whileHover={{ rotate: -6, scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${accent} text-white shadow-lg`}
                  >
                    <Icon className="w-6 h-6" strokeWidth={2.2} />
                  </motion.span>
                </div>

                {/* Title + copy */}
                <h3 className="relative text-base font-semibold text-slate-900 leading-tight mb-2">
                  {title}
                </h3>
                <p className="relative text-sm text-slate-600 leading-relaxed mb-5 flex-1">
                  {desc}
                </p>

                {/* CTA */}
                <Link
                  to={href}
                  className="relative inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 group/cta"
                  data-testid={`use-case-cta-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span>{cta}</span>
                  <motion.span
                    initial={{ x: 0 }}
                    whileHover={{ x: 3 }}
                    className="inline-flex"
                  >
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-0.5" strokeWidth={2.4} />
                  </motion.span>
                </Link>
              </motion.article>
            );
          })}

          {/* 8th tile — "+ Custom workflow" call-to-action */}
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.4, delay: 0.05 + USE_CASES.length * 0.05 }}
            whileHover={{ y: -6 }}
            className="group relative rounded-2xl border-2 border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-violet-50/40 p-6 hover:from-indigo-100/60 hover:to-violet-100/60 hover:border-indigo-300 transition-all duration-300 overflow-hidden flex flex-col"
            data-testid="use-case-card-custom"
          >
            <div className="relative mb-4">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white text-indigo-600 ring-2 ring-indigo-200 shadow-sm">
                <span className="text-2xl font-bold leading-none">+</span>
              </span>
            </div>
            <h3 className="relative text-base font-semibold text-slate-900 leading-tight mb-2">
              Your custom workflow
            </h3>
            <p className="relative text-sm text-slate-600 leading-relaxed mb-5 flex-1">
              Have something else in mind? Build any form your business needs.
            </p>
            <Link
              to="/signup"
              className="relative inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              data-testid="use-case-cta-custom"
            >
              <span>Start from scratch</span>
              <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
            </Link>
          </motion.article>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
